import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Chip, TextField, Label, Input } from '@heroui/react';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EventLogDetails } from '../utils/event-log-details';
import { getTopLevelDomain } from '../utils/get-top-level-domain';
import logo from '../assets/logo-dark-colored.svg';

import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { GridApi, ICellRendererParams, ColDef, themeQuartz } from 'ag-grid-community';

type CrawlrunSearch = {
  crawlRunId?: string;
  apiKey?: string;
};

export const Route = createFileRoute('/')({
  component: IndexPage,
  validateSearch: (search: Record<string, unknown>): CrawlrunSearch => {
    // validate and parse the search params into a typed state
    return {
      crawlRunId: (search.crawlRunId as string) || '',
      apiKey: search._apikey as string,
    };
  },
});

export function formatState(keyValue: string) {
  if (keyValue === 'DATA') {
    return (
      <Chip
        size='sm'
        color='success'
        variant='soft'
      >
        <Chip.Label>{keyValue}</Chip.Label>
      </Chip>
    );
  }
  if (keyValue === 'INTERACTION' || keyValue === 'BLOCKED' || keyValue === 'CRASHED') {
    return (
      <Chip
        size='sm'
        color='danger'
        variant='soft'
      >
        <Chip.Label>{keyValue}</Chip.Label>
      </Chip>
    );
  }
  if (keyValue === 'NO_DATA') {
    return (
      <Chip
        size='sm'
        color='warning'
        variant='soft'
      >
        <Chip.Label>{keyValue}</Chip.Label>
      </Chip>
    );
  }
  return keyValue;
}

function IndexPage() {
  const navigate = useNavigate();
  const routeSearch = Route.useSearch();
  const crawlRunIdFromUrl = routeSearch?.crawlRunId ?? '';
  const apiKey = routeSearch?.apiKey ?? '';

  const [crawlrunId, setCrawlrunId] = React.useState(crawlRunIdFromUrl);
  const [crawlrunIdEdited, setCrawlrunIdEdited] = React.useState(crawlRunIdFromUrl);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  // Queries
  const query = useQuery({
    queryKey: ['query', crawlrunId, apiKey],
    refetchOnWindowFocus: false,
    refetchInterval: false,
    queryFn: async (): Promise<EventLogDetails[]> => {
      if (!crawlrunId) {
        return [];
      }

      gridApi?.showLoadingOverlay();
      try {
        const res = await fetch(
          `https://api.${getTopLevelDomain()}/eventlog_new/${crawlrunId}?${apiKey ? `_apikey=${apiKey}` : ''}`,
          {
            credentials: 'include',
          },
        );

        if (res.status === 401 || res.status === 403) {
          throw new Error('ACCESS_DENIED');
        }

        const data = await res.json();

        if (data.code) {
          if (data.code === 2004) {
            window.location.href = `https://enter.${getTopLevelDomain()}/?redirect=${encodeURIComponent(window.location.href)}`;
          }
          throw new Error(data.message);
        }

        return data.map((item: EventLogDetails) => {
          const meta = item.meta;
          const proxyParts = [meta?.proxyType, meta?.proxyCountry].filter(Boolean);
          return {
            ...item,
            state: meta?.state ?? meta?.status ?? item.state,
            version: (meta?.version ?? item.version)?.substring(0, 6) ?? 'nightmare',
            timestamp: new Date(item.timestamp).toLocaleString(),
            proxy: proxyParts.length > 0 ? proxyParts.join(' / ') : undefined,
            browser: meta?.browser,
          };
        });
      } finally {
        gridApi?.hideOverlay();
      }
    },
  });

  const loadingTemplate = '<span class="ag-overlay-loading-center">Loading...</span>';
  const noRowsTemplate = '<span class="ag-overlay-no-rows-center">No rows to display</span>';

  // Check if data contains browser/version fields
  const hasBrowserData = useMemo(() => {
    return query.data?.some((item) => item.browser) ?? false;
  }, [query.data]);

  const hasVersionData = useMemo(() => {
    return query.data?.some((item) => item.version && item.version !== 'nightmare') ?? false;
  }, [query.data]);

  // Column Definitions: Defines the columns to be displayed.
  const colDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      { field: 'taskId', width: 300, cellStyle: { fontSize: '12px' } },
      {
        field: 'retry',
        width: 80,
        floatingFilter: true,
        filter: 'agNumberColumnFilter',
        sortable: true,
      },
      {
        field: 'state',
        width: 160,
        filter: true,
        sortable: true,
        floatingFilter: true,
        cellRenderer: (params: ICellRendererParams) => {
          return formatState(params.value);
        },
      },
      { field: 'proxy', width: 180, floatingFilter: true, filter: true, sortable: true },
    ];

    if (hasBrowserData) {
      cols.push({ field: 'browser', width: 110, floatingFilter: true, filter: true, sortable: true });
    }

    if (hasVersionData) {
      cols.push({
        field: 'version',
        width: 100,
        floatingFilter: true,
        filter: true,
        sortable: true,
        cellStyle: { fontSize: '12px' },
      });
    }

    cols.push({ field: 'timestamp', width: 200, sortable: true, cellStyle: { fontSize: '12px' } });

    return cols;
  }, [hasBrowserData, hasVersionData]);

  return (
    <div>
      <div id='logo-container'>
        <div className='max-w-6xl mx-auto px-6 py-4 flex items-center justify-between'>
          <img
            width={132}
            alt='Import.io'
            src={logo}
          />
          <div className='eyebrow'>Crawlrun · Debugger</div>
        </div>
      </div>

      <div className='max-w-6xl mx-auto px-6 pt-8 pb-6'>
        <div className='surface p-5 flex gap-4 items-end'>
          <TextField
            className='flex-1'
            value={crawlrunIdEdited}
            onChange={setCrawlrunIdEdited}
          >
            <Label>Crawlrun guid / Crawlrun:task guid</Label>
            <Input type='text' />
          </TextField>
          <button
            className='btn-primary'
            onClick={() => {
              setCrawlrunId(crawlrunIdEdited);
              void query.refetch();
            }}
            disabled={query.isLoading}
          >
            {query.isLoading ? 'Loading…' : 'Submit'}
          </button>
        </div>
      </div>

      <div className='max-w-6xl mx-auto px-6 pb-12'>
        {query.isError && query.error.message === 'ACCESS_DENIED' ? (
          <div className='surface p-6 text-center'>
            <div className='eyebrow mb-2 text-[var(--danger)]'>Access denied</div>
            <div className='display text-xl mb-2'>You don&apos;t have permission to view this crawlrun</div>
            <div className='text-sm text-[var(--ink-500)]'>
              Check your credentials or contact your administrator.
            </div>
          </div>
        ) : query.isError ? (
          <div className='surface p-4 flex items-center gap-3'>
            <Chip color='danger' variant='soft'>
              <Chip.Label>Error</Chip.Label>
            </Chip>
            <span className='mono text-[var(--ink-700)]'>{query.error.message}</span>
          </div>
        ) : null}
        {!query.isError && (
          <div className='surface overflow-hidden'>
            <div className='flex items-center justify-between px-4 py-2 border-b border-[var(--ink-200)]'>
              <div className='eyebrow'>Tasks</div>
              <div className='mono text-[var(--ink-500)] text-[11px]'>
                {query.data?.length ?? 0} {query.data?.length === 1 ? 'row' : 'rows'}
              </div>
            </div>
            <div style={{ height: 500, width: '100%' }}>
              <AgGridReact
                theme={themeQuartz}
                rowData={query.data}
                columnDefs={colDefs}
                onGridReady={(params) => {
                  setGridApi(params.api);
                }}
                onRowClicked={(event) => {
                  void navigate({
                    to: '/player',
                    search: {
                      crawlrunGuid: event.data!.crawlRunId,
                      taskGuid: event.data!.taskId,
                      key: event.data!.Key,
                      ...(apiKey && { _apikey: apiKey }),
                    },
                  });
                }}
                overlayLoadingTemplate={loadingTemplate}
                overlayNoRowsTemplate={noRowsTemplate}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
